import {
  FilterMenuBase,
  FilterMenuSection,
} from '@/components/FilterMenu/FilterMenuBase'
import { FilterMenuInput } from '@/components/FilterMenu/FilterMenuInput'
import { useAffectedUserLookup } from '@/features/AuditLogs/hooks/useAffectedUserLookup'
import type {
  AffectedUserLookup,
  AffectedUserMatch,
} from '@/features/AuditLogs/lib/affectedUserLookup'
import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react'
import {
  type KeyboardEvent,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

type AffectedUserFilterProps = {
  selectedUserId: string | null
  onApply: (userId: string, email: string | undefined) => void
}

export function AffectedUserFilter({
  selectedUserId,
  onApply,
}: Readonly<AffectedUserFilterProps>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const inputRef = useRef<HTMLInputElement>(null)
  const [contentKey, setContentKey] = useState(0)

  const onMenuOpen = useCallback(() => {
    setTimeout(() => inputRef.current?.focus(), 10)
  }, [])

  const onMenuClose = useCallback(() => setContentKey((key) => key + 1), [])

  return (
    <FilterMenuBase
      buttonLabel={String(t('auditLogs.filters.affectedUser.label'))}
      hasSelectedValue={selectedUserId !== null}
      menuWidth={400}
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
    >
      {({ closeMenu }) => (
        <AffectedUserFilterContent
          key={contentKey}
          closeMenu={closeMenu}
          inputRef={inputRef}
          onApply={onApply}
        />
      )}
    </FilterMenuBase>
  )
}

type AffectedUserFilterContentProps = {
  closeMenu: () => void
  inputRef: RefObject<HTMLInputElement | null>
  onApply: (userId: string, email: string | undefined) => void
}

function AffectedUserFilterContent({
  closeMenu,
  inputRef,
  onApply,
}: Readonly<AffectedUserFilterContentProps>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const [text, setText] = useState('')
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null)
  const { lookup, refetch } = useAffectedUserLookup(submittedEmail)

  const onApplyRef = useRef(onApply)
  const closeMenuRef = useRef(closeMenu)
  useLayoutEffect(() => {
    onApplyRef.current = onApply
    closeMenuRef.current = closeMenu
  })

  const singleMatchId = lookup.kind === 'single' ? lookup.match.id : null
  useEffect(() => {
    if (singleMatchId === null || submittedEmail === null) {
      return
    }
    setSubmittedEmail(null)
    onApplyRef.current(singleMatchId, submittedEmail)
    closeMenuRef.current()
  }, [singleMatchId, submittedEmail])

  const submit = () => {
    const trimmed = text.trim()
    if (trimmed === '') {
      return
    }
    if (trimmed.includes('@')) {
      setSubmittedEmail(trimmed.toLowerCase())
      return
    }
    onApply(trimmed, undefined)
    closeMenu()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      submit()
      return
    }
    event.stopPropagation()
  }

  const pick = (match: AffectedUserMatch) => {
    onApply(match.id, submittedEmail ?? undefined)
    closeMenu()
  }

  return (
    <FilterMenuSection>
      <FilterMenuInput
        value={text}
        inputRef={inputRef}
        placeholder={String(t('auditLogs.filters.affectedUser.placeholder'))}
        onChange={(event) => {
          setText(event.target.value)
          setSubmittedEmail(null)
        }}
        onClear={() => {
          setText('')
          setSubmittedEmail(null)
        }}
        onKeyDown={handleKeyDown}
      />
      <Text fontSize="xs" color="gray.500" mt={1}>
        {t('auditLogs.filters.affectedUser.hint')}
      </Text>
      <Box aria-live="polite" mt={2} onKeyDown={keepKeysInLookupStatus}>
        <LookupStatus lookup={lookup} onRetry={refetch} onPick={pick} />
      </Box>
    </FilterMenuSection>
  )
}

function keepKeysInLookupStatus(event: KeyboardEvent<HTMLDivElement>) {
  if (event.key !== 'Escape') {
    event.stopPropagation()
  }
}

type LookupStatusProps = {
  lookup: AffectedUserLookup
  onRetry: () => void
  onPick: (match: AffectedUserMatch) => void
}

function LookupStatus({
  lookup,
  onRetry,
  onPick,
}: Readonly<LookupStatusProps>) {
  const { t } = useTranslation('features', { useSuspense: false })
  switch (lookup.kind) {
    case 'idle':
    case 'single':
      return null
    case 'loading':
      return (
        <Text fontSize="sm" color="text.text-subtle">
          {t('auditLogs.filters.affectedUser.lookup.loading')}
        </Text>
      )
    case 'none':
      return (
        <Text fontSize="sm" color="text.text-subtle">
          {t('auditLogs.filters.affectedUser.lookup.none')}
        </Text>
      )
    case 'error':
      return (
        <HStack gap={2} align="center">
          <Text fontSize="sm" color="content.error">
            {t('auditLogs.filters.affectedUser.lookup.error')}
          </Text>
          <Button size="xs" variant="outline" onClick={() => onRetry()}>
            {t('auditLogs.filters.affectedUser.lookup.retry')}
          </Button>
        </HStack>
      )
    case 'several':
      return (
        <>
          <Text fontSize="sm" color="text.text-subtle" mb={1}>
            {t('auditLogs.filters.affectedUser.lookup.several')}
          </Text>
          <VStack align="stretch" gap={1} maxHeight="240px" overflowY="auto">
            {lookup.matches.map((match) => (
              <Button
                key={match.id}
                variant="ghost"
                size="sm"
                width="full"
                height="auto"
                justifyContent="start"
                py={2}
                onClick={() => onPick(match)}
              >
                <MatchSummary match={match} />
              </Button>
            ))}
          </VStack>
        </>
      )
  }
}

function MatchSummary({ match }: Readonly<{ match: AffectedUserMatch }>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const orgsLine =
    match.orgs.length === 0
      ? t('auditLogs.filters.affectedUser.match.noOrgs')
      : t('auditLogs.filters.affectedUser.match.orgs', {
          orgs: match.orgs
            .map((org) => `${org.org_name} (${org.org_id})`)
            .join(', '),
        })
  return (
    <VStack align="start" gap={0}>
      <Text fontFamily="mono" fontSize="sm" wordBreak="break-all">
        {match.id}
      </Text>
      <Text
        fontSize="xs"
        color="text.text-subtle"
        whiteSpace="normal"
        textAlign="start"
      >
        {orgsLine}
      </Text>
    </VStack>
  )
}
